import CircularProgress from "@material-ui/core/CircularProgress"
import CalendarView from "../calendar-events/components/CalendarView"
import { NewCalendarEventData, useGetCalendarEventsQuery } from "../calendar-events/api"
import { useCalendarEventMutations } from "../calendar-events/hooks/use-calendar-event-mutations"
import { useAppContext } from "../app-context/use-app-context"
import { useAuthUserMembership } from "../memberships/hooks/use-auth-user-membership"
import { useGetTasksQuery } from "../tasks/api"
import { NewTodoData, useGetTodosQuery } from "../todos/api"
import { useTodoMutations } from "../todos/hooks/use-todo-mutations"
import {
  getCalendarEventQueryFromContext,
  getTodoQueryFromContext,
  getTaskQueryFromContext,
} from "../context-options/utils"
import ErrorList from "../components/ErrorList"
import { useCalendarSettings } from "../hooks/use-calendar-settings"
import { useTranslation } from "react-i18next"

const queryOptions = { refetchOnMountOrArgChange: true } as const

const useLoader = () => {
  const translations = useTranslations()
  const routeQueryErrors = {} as QueryErrors
  const { appContext } = useAppContext()
  const { mainContext, subContext } = appContext
  const context = subContext || mainContext
  const skipFetchingTodos = context?.type === "supplier"

  const calendarEventQuery = getCalendarEventQueryFromContext(context)
  const todoQuery = getTodoQueryFromContext(context)
  const taskQuery = getTaskQueryFromContext(context)

  const {
    data: calendarEvents = [],
    isLoading: isLoadingCalendarEvents,
    isError: isErrorCalendarEvents,
  } = useGetCalendarEventsQuery(calendarEventQuery, queryOptions)

  const {
    data: tasks = [],
    isLoading: isLoadingTasks,
    isError: isErrorTasks,
  } = useGetTasksQuery(taskQuery, queryOptions)

  const {
    data: todos = [],
    isLoading: isLoadingTodos,
    isError: isErrorTodos,
  } = useGetTodosQuery(todoQuery, { ...queryOptions, skip: skipFetchingTodos })

  const {
    settingsKey,
    calendarSettings,
    queryErrors: calendarSettingsQueryErrors,
    isError: isErrorCalendarSettings,
    isLoading: isLoadingCalendarSettings,
  } = useCalendarSettings({ mainContext, skipAll: !mainContext })

  const isLoading = isLoadingCalendarEvents || isLoadingTasks || isLoadingTodos || isLoadingCalendarSettings
  const isError = isErrorCalendarEvents || isErrorTasks || isErrorTodos || isErrorCalendarSettings

  if (isErrorCalendarEvents) routeQueryErrors.calendarEvent = translations.fetchErrorCalendarEvents
  if (isErrorTasks) routeQueryErrors.task = translations.fetchErrorTasks
  if (isErrorTodos) routeQueryErrors.todo = translations.fetchErrorTodos

  const queryErrors = {
    ...routeQueryErrors,
    ...calendarSettingsQueryErrors,
  }

  return {
    isLoading,
    isError,
    context,
    mainContext,
    calendarEvents,
    tasks,
    todos,
    settingsKey,
    calendarSettings,
    queryErrors,
  }
}

const CalendarRoute = () => {
  const { isLoading, isError, queryErrors, context, mainContext, calendarEvents, tasks, todos, calendarSettings } =
    useLoader()
  const { canCreateCalendarEvents, canCreateTodos } = useAuthUserMembership()
  const { createCalendarEvent } = useCalendarEventMutations()
  const { createTodo } = useTodoMutations()
  const translations = useTranslations()

  if (isLoading) return <CircularProgress />
  if (!context || !mainContext) return <ErrorList errors={[translations.contextNotFoundError]} />
  if (isError) return <ErrorList errors={queryErrors} />

  const canAddCalendarEvents = mainContext.type === "user" || canCreateCalendarEvents
  const canAddTodos = (mainContext.type === "user" || canCreateTodos) && context.type !== "supplier"

  const onAddCalendarEvent = (calendarEventData: NewCalendarEventData) => {
    const newEventData = { ...calendarEventData }
    if (mainContext.type === "org") newEventData.organisation = mainContext.id
    if (context.type === "customer") newEventData.customers = [context.id]
    if (context.type === "member") newEventData.participants = [context.id]
    if (context.type === "workspace") newEventData.workspaces = [context.id]
    if (context.type === "supplier") newEventData.suppliers = [context.id]
    return createCalendarEvent(newEventData)
  }

  const onAddTodo = (todoData: NewTodoData) => {
    const newTodoData = { ...todoData }
    if (mainContext.type === "org") newTodoData.organisation = mainContext.id
    if (context.type === "customer") newTodoData.customers = [context.id]
    if (context.type === "member") newTodoData.responsible = [context.id]
    if (context.type === "workspace") newTodoData.workspaces = [context.id]
    return createTodo(newTodoData)
  }

  return (
    <CalendarView
      id={context.id}
      title={context.name}
      calendarEvents={calendarEvents}
      tasks={tasks}
      todos={context.type === "supplier" ? [] : todos}
      calendarSettings={calendarSettings}
      canAddCalendarEvents={canAddCalendarEvents}
      canAddTasks={false}
      canAddTodos={canAddTodos}
      onAddCalendarEvent={onAddCalendarEvent}
      onAddTask={doNothing}
      onAddTodo={onAddTodo}
      showCaption
    />
  )
}

async function doNothing() {}

export default CalendarRoute

const useTranslations = () => {
  const { t } = useTranslation("translation")

  return {
    contextNotFoundError: t("contextNotFoundError", { defaultValue: "Context not found" }),
    fetchErrorCalendarEvents: t("fetchErrorCalendarEvents", { defaultValue: "Failed to fetch calendar events" }),
    fetchErrorTasks: t("fetchErrorTasks", { defaultValue: "Failed to fetch tasks" }),
    fetchErrorTodos: t("fetchErrorTodos", { defaultValue: "Failed to fetch todos" }),
  }
}

type QueryErrors = {
  calendarEvent?: string
  task?: string
  todo?: string
  calendarSettings?: string
}
